import type { PlayerStats } from "../game/stats";

// Data-driven upgrades. Each `apply` mutates the player's stat block.
// Philosophy (per brief): builds should become broken — stacking is intended.
export interface UpgradeDef {
  id: string;
  name: string;
  description: string;
  color: string;
  /** Vector icon key (see ui/icons.ts drawIcon). */
  icon: string;
  /** Higher = appears more often in the choose-3 roll. */
  weight: number;
  /** Times this upgrade can be taken (0 = unlimited). */
  maxStacks: number;
  /** Only offered once PCKT reaches this level (omitted = from the start). */
  minLevel?: number;
  apply: (s: PlayerStats) => void;
}

export const UPGRADES: UpgradeDef[] = [
  {
    id: "signal_amplifier",
    icon: "radiate",
    name: "Signal Amplifier",
    description: "+18% weapon range / zap radius",
    color: "#76D2DB",
    weight: 100,
    maxStacks: 0,
    apply: (s) => (s.rangeMult *= 1.18),
  },
  {
    id: "edge_compute",
    icon: "chevrons",
    name: "Edge Compute",
    description: "+15% attack speed (faster cooldowns)",
    color: "#A6E9F0",
    weight: 100,
    maxStacks: 0,
    apply: (s) => (s.attackSpeed *= 1.15),
  },
  {
    id: "packet_compression",
    icon: "arrow",
    name: "Packet Compression",
    description: "+25% projectile speed",
    color: "#E68A4E",
    weight: 80,
    maxStacks: 0,
    apply: (s) => (s.projectileSpeedMult *= 1.25),
  },
  {
    id: "prediction_engine",
    icon: "target",
    name: "Prediction Engine",
    description: "+8% critical chance",
    color: "#EAD49A",
    weight: 80,
    maxStacks: 0,
    apply: (s) => (s.critChanceAdd += 0.08),
  },
  {
    id: "overclock",
    icon: "bolt",
    name: "Overclock",
    description: "+20% damage",
    color: "#DA4848",
    weight: 100,
    maxStacks: 0,
    apply: (s) => (s.damageMult *= 1.2),
  },
  {
    id: "threat_correlation",
    icon: "chain",
    name: "Threat Correlation",
    description: "+1 chain / pierce target",
    color: "#B084CB",
    weight: 60,
    maxStacks: 0,
    apply: (s) => {
      s.chainAdd += 1;
      s.pierceAdd += 1;
    },
  },
  {
    id: "signal_duplication",
    icon: "split",
    name: "Signal Duplication",
    description: "+1 projectile / zap target",
    color: "#E0609B",
    weight: 45,
    maxStacks: 0,
    apply: (s) => (s.countAdd += 1),
  },
  {
    id: "autonomous_routing",
    icon: "boots",
    name: "Autonomous Routing",
    description: "+12% move speed",
    color: "#BED4CB",
    weight: 70,
    maxStacks: 0,
    apply: (s) => (s.moveSpeedMult *= 1.12),
  },
  {
    id: "recovery_cache",
    icon: "shield",
    name: "Recovery Cache",
    description: "+20 max integrity & full repair",
    color: "#BED4CB",
    weight: 70,
    maxStacks: 0,
    apply: (s) => {
      s.maxIntegrityAdd += 20;
      s.healOnPickup += 0; // hook for later
    },
  },
  {
    id: "magnet_array",
    icon: "magnet",
    name: "Magnet Array",
    description: "+40% fragment pickup radius",
    color: "#EAD49A",
    weight: 60,
    maxStacks: 0,
    apply: (s) => (s.pickupRadiusMult *= 1.4),
  },
  {
    id: "regen_protocol",
    icon: "cross",
    name: "Regen Protocol",
    description: "+0.6 integrity / sec regeneration",
    color: "#BED4CB",
    weight: 40,
    maxStacks: 0,
    apply: (s) => (s.regenPerSec += 0.6),
  },
  {
    id: "kinetic_packets",
    icon: "burst",
    name: "Kinetic Packets",
    description: "+60% knockback",
    color: "#E68A4E",
    weight: 35,
    maxStacks: 0,
    apply: (s) => (s.knockbackMult *= 1.6),
  },
  {
    // Late-game defensive option. Deliberately slower than a single attacker's
    // node DPS and capped, so self-healing nodes can always still be overrun.
    id: "self_repair_mesh",
    icon: "mesh",
    name: "Self-Repair Mesh",
    description: "Online nodes slowly self-heal (+1.5 stability/sec)",
    color: "#79dac8",
    weight: 45,
    maxStacks: 3,
    minLevel: 10,
    apply: (s) => (s.nodeRegenPerSec += 1.5),
  },
];
